/**
 * Image -> CANVAS_SIZE^2 RGB tensor matching what the TripoSplat encoders
 * (DINOv3 + Flux2 VAE) expect. CANVAS_SIZE is 1024 to match upstream
 * training; flow attention buffers are kept in bounds inside the exports
 * by Q-chunked SDPA.
 *
 * Steps:
 *   1. Load image, resize so the shorter side = CANVAS_SIZE.
 *   2. Run background removal (or trust the alpha if the source had one).
 *   3. Erode the alpha by ~1px to kill segmentation-border bleed.
 *   4. Crop to subject bbox with a 1.2x margin, square it, resize to CANVAS_SIZE.
 *   5. Composite on black, return RGB.
 *
 * RMBG-1.4 always runs at 1024^2 because that's what its ONNX is exported for;
 * we resize back down after extracting the matte.
 */
import * as ort from "onnxruntime-web";
import type { InferenceSession } from "onnxruntime-web";

// 1024^2 to match upstream training. WebGPU buffer caps are handled inside
// each encoder by Q-chunked attention (see export_dinov3.py and
// export_flow_model.py).
const CANVAS_SIZE = 1024;

export interface PreparedImage {
  /** RGB tensor (1, 3, 1024, 1024) in [-1, 1] for the VAE encoder. */
  rgbMinus1to1: Float32Array;
  /** RGB tensor (1, 3, 1024, 1024) ImageNet-normalized for DINOv3. */
  rgbImagenet:  Float32Array;
  /** A copy of the post-preprocess image for preview. */
  previewBlob:  Blob;
}

/** Raw mode -- skip RMBG / crop / composite. Load the image, resize to
 *  1024x1024 with the same Canvas2D LANCZOS-ish path, and emit the two
 *  tensors. Use this to feed the web encoders the same RGB that
 *  python_debug.py wrote to /tmp/prep_python.png, so feat1/feat2 should
 *  agree to within model dtype slop. */
export async function preprocessImageRaw(file: File): Promise<PreparedImage> {
  const bmp = await createImageBitmap(file);
  const c = new OffscreenCanvas(CANVAS_SIZE, CANVAS_SIZE);
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // Stretch directly to 1024x1024. The python reference's prep_python.png
  // is already 1024x1024, so this is a no-op resize in that case.
  ctx.drawImage(bmp, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
  const id = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  const previewBlob = await canvasToBlob(c);
  return {
    rgbMinus1to1: imageDataToFloat(id, [-1, 1]),
    rgbImagenet:  imageDataToImagenet(id),
    previewBlob,
  };
}

export interface PreprocessOptions {
  /** Erode the alpha mask by N pixels to kill segmentation-border bleed.
   *  Default 1. Larger values shrink the subject (cleaner crop but loses
   *  the edge of the model). */
  erodeRadius?: number;
  /** Square-crop margin around the subject bbox. 1.0 = tight, 1.5 = 25%
   *  padding on each side. Default 1.2 (matches the python reference). */
  cropMargin?: number;
  /** Minimum alpha for a pixel to count toward the subject's bounding box.
   *  Default 8. Higher values ignore semi-transparent halo; lower values
   *  include fainter pixels. */
  bboxThreshold?: number;
}

export async function preprocessImage(file: File,
                                       rmbg: InferenceSession | null,
                                       opts: PreprocessOptions = {}): Promise<PreparedImage> {
  const erodeRadius   = opts.erodeRadius   ?? 1;
  const cropMargin    = opts.cropMargin    ?? 1.2;
  const bboxThreshold = opts.bboxThreshold ?? 8;
  const bmp = await createImageBitmap(file);

  // 1. resize so shorter side = 1024
  const scale = CANVAS_SIZE / Math.min(bmp.width, bmp.height);
  const w = Math.max(1, Math.round(bmp.width  * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const resized = drawTo(bmp, w, h);

  // 2. alpha matte
  let alpha: Uint8ClampedArray;
  if (file.type === "image/png" && hasAlpha(resized)) {
    alpha = readAlpha(resized);
  } else if (rmbg) {
    alpha = await runRmbg(rmbg, resized);
  } else {
    // No matte and no RMBG -> fall back to full opaque (subject must already
    // be on a clean background).
    alpha = new Uint8ClampedArray(w * h).fill(255);
  }

  // 3. erode
  if (erodeRadius > 0) alpha = erode(alpha, w, h, erodeRadius);

  // 4. crop to bbox + 20% margin, square, resize to 1024, composite on black.
  // Order matters: the python reference resizes the RGBA crop with LANCZOS
  // and only then alpha-composites onto a black background. Premultiplying
  // before the resize bleeds the black bg through the resampler and softens
  // subject edges differently than the reference -- subtle but it shifts
  // the DINOv3 / VAE feature stats.
  const bb = bbox(alpha, w, h, bboxThreshold);
  if (!bb) throw new Error("background removal returned an empty mask");
  const cx = (bb.x0 + bb.x1) / 2, cy = (bb.y0 + bb.y1) / 2;
  const half = Math.max(bb.x1 - bb.x0, bb.y1 - bb.y0) / 2 * cropMargin;
  const x0 = Math.round(cx - half), y0 = Math.round(cy - half);
  const side = Math.round(half * 2);
  const cropped = cropRgba(resized, alpha, x0, y0, side, side);

  const finalCanvas = compositeOnBlack(cropped, CANVAS_SIZE);
  const id = finalCanvas.getContext("2d")!.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  const previewBlob = await canvasToBlob(finalCanvas);

  return {
    rgbMinus1to1: imageDataToFloat(id, [-1, 1]),
    rgbImagenet:  imageDataToImagenet(id),
    previewBlob,
  };
}

// ----- canvas helpers -------------------------------------------------------

function drawTo(bmp: ImageBitmap, w: number, h: number): ImageData {
  const c = new OffscreenCanvas(w, h);
  const ctx = c.getContext("2d")!;
  // Reference (triposplat.py) resamples with PIL LANCZOS. Chrome's "high"
  // quality hint uses a Lanczos-like kernel on downsample, which keeps the
  // DINOv3 feature stats closer to the python reference.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bmp, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

async function canvasToBlob(c: OffscreenCanvas): Promise<Blob> {
  return await c.convertToBlob({ type: "image/png" });
}

function hasAlpha(id: ImageData): boolean {
  for (let i = 3; i < id.data.length; i += 4) {
    if (id.data[i] < 255) return true;
  }
  return false;
}

function readAlpha(id: ImageData): Uint8ClampedArray {
  const a = new Uint8ClampedArray(id.width * id.height);
  for (let i = 0, j = 3; i < a.length; i++, j += 4) a[i] = id.data[j];
  return a;
}

function bbox(alpha: Uint8ClampedArray, w: number, h: number, threshold = 8):
  { x0: number; y0: number; x1: number; y1: number } | null {
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (alpha[y * w + x] > threshold) {
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x > x1) x1 = x;
        if (y > y1) y1 = y;
      }
    }
  }
  return x1 < x0 ? null : { x0, y0, x1, y1 };
}

function erode(a: Uint8ClampedArray, w: number, h: number, r: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(a.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let m = 255;
      for (let dy = -r; dy <= r; dy++) {
        const yy = y + dy; if (yy < 0 || yy >= h) { m = 0; break; }
        for (let dx = -r; dx <= r; dx++) {
          const xx = x + dx; if (xx < 0 || xx >= w) { m = 0; break; }
          const v = a[yy * w + xx]; if (v < m) m = v;
        }
        if (m === 0) break;
      }
      out[y * w + x] = m;
    }
  }
  return out;
}

/** Crop an RGBA region (out-of-bounds = transparent black). Matches PIL's
 *  Image.crop semantics: source alpha is carried through, not multiplied
 *  into RGB. The composite-on-black step happens after the LANCZOS resize. */
function cropRgba(rgb: ImageData, alpha: Uint8ClampedArray,
                  x0: number, y0: number, w: number, h: number): ImageData {
  const out = new ImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const sx = x0 + x, sy = y0 + y;
      const oi = (y * w + x) * 4;
      if (sx < 0 || sy < 0 || sx >= rgb.width || sy >= rgb.height) {
        out.data[oi] = 0; out.data[oi+1] = 0; out.data[oi+2] = 0; out.data[oi+3] = 0;
        continue;
      }
      const si = (sy * rgb.width + sx) * 4;
      out.data[oi]   = rgb.data[si];
      out.data[oi+1] = rgb.data[si+1];
      out.data[oi+2] = rgb.data[si+2];
      out.data[oi+3] = alpha[sy * rgb.width + sx];
    }
  }
  return out;
}

/** Resize the RGBA crop to (size, size) with high-quality (Lanczos-ish)
 *  resampling, then composite onto opaque black. Mirrors the reference:
 *      image = image.resize((size, size), Image.LANCZOS)
 *      bg = Image.new("RGB", (size, size), (0,0,0))
 *      bg.paste(image, mask=image.split()[3])  # out = image*a (bg=black) */
function compositeOnBlack(src: ImageData, size: number): OffscreenCanvas {
  const tmp = new OffscreenCanvas(src.width, src.height);
  tmp.getContext("2d")!.putImageData(src, 0, 0);
  const resized = new OffscreenCanvas(size, size);
  const rctx = resized.getContext("2d")!;
  rctx.imageSmoothingEnabled = true;
  rctx.imageSmoothingQuality = "high";
  rctx.drawImage(tmp, 0, 0, size, size);
  const rid = rctx.getImageData(0, 0, size, size);

  // Composite onto black, premultiplying RGB by alpha. We re-emit as RGBA
  // with alpha=255 so the downstream `getImageData` reads opaque RGB.
  const data = rid.data;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] / 255;
    data[i]     = Math.round(data[i]     * a);
    data[i + 1] = Math.round(data[i + 1] * a);
    data[i + 2] = Math.round(data[i + 2] * a);
    data[i + 3] = 255;
  }
  rctx.putImageData(rid, 0, 0);
  return resized;
}

function imageDataToFloat(id: ImageData, range: [number, number]): Float32Array {
  const N = id.width * id.height;
  const out = new Float32Array(3 * N);
  const [lo, hi] = range;
  for (let i = 0, j = 0; i < N; i++, j += 4) {
    out[0 * N + i] = lo + (id.data[j]   / 255) * (hi - lo);
    out[1 * N + i] = lo + (id.data[j+1] / 255) * (hi - lo);
    out[2 * N + i] = lo + (id.data[j+2] / 255) * (hi - lo);
  }
  return out;
}

function imageDataToImagenet(id: ImageData): Float32Array {
  const N = id.width * id.height;
  const out = new Float32Array(3 * N);
  const M = [0.485, 0.456, 0.406], S = [0.229, 0.224, 0.225];
  for (let i = 0, j = 0; i < N; i++, j += 4) {
    out[0 * N + i] = ((id.data[j]   / 255) - M[0]) / S[0];
    out[1 * N + i] = ((id.data[j+1] / 255) - M[1]) / S[1];
    out[2 * N + i] = ((id.data[j+2] / 255) - M[2]) / S[2];
  }
  return out;
}

async function runRmbg(rmbg: InferenceSession, id: ImageData): Promise<Uint8ClampedArray> {
  // RMBG-1.4 expects (1, 3, 1024, 1024) in [0, 1]. Returns (1, 1, 1024, 1024) sigmoid alpha.
  // We resize the matte back to the input size before erosion.
  const c1 = new OffscreenCanvas(1024, 1024);
  c1.getContext("2d")!.putImageData(id, 0, 0,
    Math.max(0, (1024 - id.width)  / 2),
    Math.max(0, (1024 - id.height) / 2),
    Math.min(id.width, 1024),
    Math.min(id.height, 1024));
  const rid = c1.getContext("2d")!.getImageData(0, 0, 1024, 1024);
  const input = imageDataToFloat(rid, [0, 1]);
  const inputT = new ort.Tensor("float32", input, [1, 3, 1024, 1024]);
  const out = await rmbg.run({ input: inputT });
  const alphaT = out[Object.keys(out)[0]] as ort.Tensor;
  const data = alphaT.data as Float32Array;
  const alpha = new Uint8ClampedArray(id.width * id.height);
  for (let y = 0; y < id.height; y++) {
    for (let x = 0; x < id.width; x++) {
      const sx = x + Math.max(0, (1024 - id.width)  / 2);
      const sy = y + Math.max(0, (1024 - id.height) / 2);
      alpha[y * id.width + x] = Math.round(data[Math.floor(sy) * 1024 + Math.floor(sx)] * 255);
    }
  }
  return alpha;
}
